import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { combineLatest, map, Subject, take, takeUntil, tap } from 'rxjs';
import {
  PrintAttributeListFilters,
  PrintAttributeService,
} from '../../../services/print-attribute.service';
import { Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import {
  PrintAttributeDefinition,
  PrintAttributeType,
} from '../../../models/print-attribute.model';
import { PageEvent } from '@angular/material/paginator';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { ConfirmDialogComponent } from 'src/app/@shared';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { DivisionService } from '../../../services/division.service';
import { EventTypeService } from '../../../services/event-type.service';
import { EventWorkflowService } from '../../../services/event-workflow.service';
import { AuthorizationService } from 'src/app/@shared/services/authorization.service';

interface FilterChip {
  key: keyof PrintAttributeListFilters;
  id: string;
  label: string;
}

@Component({
  selector: 'app-print-attributes-list-table',
  templateUrl: './print-attributes-list-table.component.html',
  styleUrls: ['./print-attributes-list-table.component.scss'],
})
export class PrintAttributesListTableComponent implements OnInit, OnDestroy {
  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  @ViewChild('filterSidenav', { read: ElementRef }) filterSidenavElement?: ElementRef<HTMLElement>;
  @ViewChild('filterToggleButton', { read: ElementRef }) filterToggleButton?: ElementRef<HTMLElement>;
  private destroy$ = new Subject<void>();

  divisionFilterControl = new FormControl<string[]>([]);
  attributeTypeFilterControl = new FormControl<string[]>([]);
  eventTypeFilterControl = new FormControl<string[]>([]);

  divisionOptions: { id: string; name: string }[] = [];
  eventTypeOptions: { id: string; name: string }[] = [];
  attributeTypeOptions: { id: string; label: string }[] = [];

  viewModel$ = combineLatest([
    this.printAttributeService.printAttributes$,
    this.printAttributeService.page$,
    this.printAttributeService.totalRecords$,
  ]).pipe(
    tap(([attributes, page, totalRecords]) => {
      this.selection.clear();
    }),
    map(([attributes, page, totalRecords]) => ({
      attributes,
      page,
      totalRecords,
    })),
  );

  onFilterChange(): void {
    this.selection.clear();
  }

  toggleFilters(): void {
    this.sidenav?.toggle();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.sidenav?.opened) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.filterSidenavElement?.nativeElement.contains(target)) return;
    if (this.filterToggleButton?.nativeElement.contains(target)) return;
    if (target.closest('.cdk-overlay-container')) return;

    this.sidenav.close();
  }

  getPrintAttributeTypeLabel(typeId: string): string {
    const type = this.attributeTypeOptions.find(t => t.id === typeId);
    return type ? type.label : typeId;
  }

  applyFilters(): void {
    const filters = this.readFiltersFromControls();
    this.printAttributeService.setListFilters(filters);
    this.toggleFilters();
  }

  clearFilters(): void {
    this.patchFilterControls({
      divisionIds: [],
      attributeTypes: [],
      eventTypeIds: [],
    });
    this.printAttributeService.clearListFilters();
    this.selection.clear();
  }

  resetFilters(): void {
    this.clearAllFilters();
    this.toggleFilters();
  }

  activeFilterChips: FilterChip[] = [];

  trackChip = (_: number, chip: FilterChip) => chip.key + ':' + chip.id;

  private refreshFilterChips(): void {
    const filters = this.printAttributeService.getListFilters();
    this.activeFilterChips = [
      ...filters.divisionIds.map((id) => ({
        key: 'divisionIds' as const,
        id,
        label: this.divisionOptions.find((d) => d.id === id)?.name ?? id,
      })),
      ...filters.attributeTypes.map((id) => ({
        key: 'attributeTypes' as const,
        id,
        label: this.getPrintAttributeTypeLabel(id),
      })),
      ...filters.eventTypeIds.map((id) => ({
        key: 'eventTypeIds' as const,
        id,
        label: this.eventTypeOptions.find((t) => t.id === id)?.name ?? id,
      })),
    ];
  }

  removeFilterChip(chip: FilterChip): void {
    const current = this.printAttributeService.getListFilters();
    const next: PrintAttributeListFilters = {
      ...current,
      [chip.key]: current[chip.key].filter((id) => id !== chip.id),
    };
    this.patchFilterControls(next);
    this.printAttributeService.setListFilters(next);
    this.selection.clear();
  }

  clearAllFilterChips(): void {
    this.clearAllFilters();
  }

  private clearAllFilters(): void {
    this.clearFilters();
    this.searchControl.setValue('');
    this.printAttributeService.search('');
  }

  printAttributes: PrintAttributeDefinition[] = [];
  selection = new SelectionModel<any>(true, []);
  displayedColumns = [
    'select',
    'Name',
    'DivisionNames',
    'EventTypeName',
    'PrintAttributeType',
    'Description',
    'BannerName',
    'Versions',
    'Actions',
  ];
  searchControl = new FormControl('');
  readOnlyMode = false;

  constructor(
    private printAttributeService: PrintAttributeService,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog,
    private divisionService: DivisionService<any>,
    private eventTypeService: EventTypeService<any>,
    private ewService: EventWorkflowService,
    private authorizationService: AuthorizationService
  ) {}

  ngOnInit(): void {
    this.printAttributeService.printAttributeTypes$.pipe(takeUntil(this.destroy$)).subscribe((types) => {
      this.attributeTypeOptions = types;
      this.refreshFilterChips();
    });

    this.readOnlyMode = !this.authorizationService.checkAccess('edit||offermang');
    if (!this.canEdit()) {
      this.displayedColumns = this.displayedColumns.filter((column) => column !== 'Actions');
    }

    this.divisionService.divisionsList$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((divisions) => {
        this.divisionOptions = divisions.map((d) => ({
          id: d.Id,
          name: d.DivisionName,
        }));
        this.refreshFilterChips();
      });

    this.eventTypeService.eventTypesList$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types) => {
        this.eventTypeOptions = types.map((t) => ({
          id: t.Id,
          name: t.EventTypeName,
        }));
        this.refreshFilterChips();
      });

    this.printAttributeService.listFilters$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshFilterChips());

    this.restoreFilters();

    this.viewModel$.pipe(takeUntil(this.destroy$)).subscribe((vm) => {
      this.printAttributes = vm.attributes;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private readFiltersFromControls(): PrintAttributeListFilters {
    return {
      divisionIds: [...(this.divisionFilterControl.value ?? [])],
      attributeTypes: [...(this.attributeTypeFilterControl.value ?? [])],
      eventTypeIds: [...(this.eventTypeFilterControl.value ?? [])],
    };
  }

  private patchFilterControls(f: PrintAttributeListFilters): void {
    this.divisionFilterControl.setValue([...f.divisionIds], {
      emitEvent: false,
    });
    this.attributeTypeFilterControl.setValue([...f.attributeTypes], {
      emitEvent: false,
    });
    this.eventTypeFilterControl.setValue([...f.eventTypeIds], {
      emitEvent: false,
    });
  }

  private restoreFilters(): void {
    this.printAttributeService.restoreSavedListFilters();
    this.searchControl.setValue(this.printAttributeService.getSearch(), {
      emitEvent: false,
    });
    this.patchFilterControls(this.printAttributeService.getListFilters());
  }

  onSearch(event: Event): void {
    this.printAttributeService.search((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.printAttributeService.search('');
    this.selection.clear();
  }

  onSort(sortState: Sort): void {
    this.printAttributeService.sort(sortState);
    this.selection.clear();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.printAttributes.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.printAttributes.forEach((row) => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected || [];
  }

  public clearSelection() {
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.printAttributeService.page(pageEvent);
  }

  deleteSelectedRecords(): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Do you want to remove Print Attribute(s)?`,
      },
      disableClose: true,
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        const selectedIds = this.getSelectedSectionRecords().map(
          (record) => record.Detail.Id,
        );
        this.printAttributeService.deletePrintAttributes(selectedIds).subscribe(() => {
          this.clearSelection();
          this.printAttributeService.reload();
          this.matSnackBar.open(
            `Print Attribute(s) deleted`,
            'OK',
            DEFAULT_SNACKBAR_CONFIG,
          );
        });
      }
    });
  }

  cloneSelectedRecords(): void {
    const selectedIds = this.getSelectedSectionRecords().map(
      (record) => record.DomainId,
    );
    this.printAttributeService.clonePrintAttributes(selectedIds).subscribe(() => {
      this.clearSelection();
      this.printAttributeService.reload();
      this.matSnackBar.open(
        `Print Attribute(s) cloned`,
        'OK',
        DEFAULT_SNACKBAR_CONFIG,
      );
    });
  }

  downloadCSV(): void {
    this.printAttributeService.downloadPrintAttributesCSV();
  }

  canEdit(): boolean {
    return !this.readOnlyMode;
  }
}
